import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight, X, Rocket } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabaseClient';
import {
  fetchSettingsByKey,
  upsertSettingsByKey,
  SETTINGS_KEYS,
} from '@/lib/settingsData';
import { useAuth } from '@/context/AuthContext';

/* ----------------------------------------------------------------------------
 * GettingStartedChecklist
 *
 * A self-guided "here's the loop" checklist for brand-new accounts, shown at the
 * top of the dashboard home. Replaces the old estimate-first onboarding push:
 * setup happens in the wizard, then the user works these at their own pace.
 *
 * - Each step auto-checks off real data (a client exists, an estimate exists…),
 *   so it reflects reality with no manual bookkeeping.
 * - The first step ("business") is usually already done from the signup wizard —
 *   an intentional early win (endowed progress).
 * - Hides itself once every step is done, or once dismissed (persisted in the
 *   blob-backed `getting_started` setting so it stays gone across devices).
 * ------------------------------------------------------------------------- */

const countRows = async (table) => {
  try {
    const { count, error } = await supabase
      .from(table)
      .select('id', { count: 'exact', head: true });
    if (error) return 0;
    return count || 0;
  } catch {
    return 0;
  }
};

export default function GettingStartedChecklist() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [steps, setSteps] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [gsRes, companyRes, clients, estimates, walkthroughs] = await Promise.all([
        fetchSettingsByKey(SETTINGS_KEYS.GETTING_STARTED, { dismissed: false }),
        fetchSettingsByKey(SETTINGS_KEYS.COMPANY, {}),
        countRows('clients'),
        countRows('estimates'),
        countRows('pqv_walkthrough'),
      ]);
      // fetchSettingsByKey returns { value, source } — unwrap before reading.
      const gs = gsRes?.value || {};
      const company = companyRes?.value || {};
      const hasBusiness = !!String(company?.name || '').trim();

      setDismissed(!!gs.dismissed);
      setSteps([
        {
          key: 'business',
          label: 'Set up your business',
          desc: 'Name, trade, and crew size — powers your proposals.',
          to: '/dashboard/settings',
          cta: 'Open settings',
          done: hasBusiness,
        },
        {
          key: 'client',
          label: 'Add your first client',
          desc: "Who you're working for. Takes 30 seconds.",
          to: '/dashboard/clients-leads',
          cta: 'Add a client',
          done: clients > 0,
        },
        {
          key: 'estimate',
          label: 'Build your first estimate',
          desc: 'The front of the whole loop — and it starts training your PQ.',
          to: '/dashboard/estimates',
          cta: 'Start an estimate',
          done: estimates > 0,
        },
        {
          key: 'pqv',
          label: 'Try a PQ Vision walkthrough',
          desc: 'Photos + notes → findings you confirm. This is the magic.',
          to: '/dashboard/pq-intelligence',
          cta: 'Open PQ Intelligence',
          done: walkthroughs > 0,
        },
      ]);
    } catch {
      // On any failure, fail quiet — never block the dashboard on a nudge card.
      setSteps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dismiss = async () => {
    setDismissed(true);
    try {
      await upsertSettingsByKey(SETTINGS_KEYS.GETTING_STARTED, { dismissed: true });
    } catch {
      /* best-effort; card is already hidden locally */
    }
  };

  // Only nudge genuinely new accounts. An established business (older account)
  // should never see the getting-started checklist — even if one optional step
  // (e.g. a PQ walkthrough) was skipped. Default to showing while the profile
  // is still loading (created_at unknown).
  const createdMs = profile?.created_at ? new Date(profile.created_at).getTime() : null;
  const isNewAccount =
    createdMs == null ? true : Date.now() - createdMs < 30 * 24 * 60 * 60 * 1000;

  if (loading || dismissed || steps.length === 0 || !isNewAccount) return null;

  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  // Everything done → the user has found their feet; stop showing the nudge.
  if (doneCount === total) return null;

  const nextStep = steps.find((s) => !s.done);
  const pct = Math.round((doneCount / total) * 100);

  return (
    <Card
      className="mb-6 border-[hsl(var(--primary))]/25 bg-[hsl(var(--card))] shadow-lg shadow-black/20"
      data-testid="getting-started-checklist"
    >
      <CardContent className="p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[hsl(var(--primary))]/15">
              <Rocket className="h-4 w-4 text-[hsl(var(--primary))]" aria-hidden />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground leading-tight">
                Getting started
              </h2>
              <p className="text-xs text-muted-foreground">
                A few steps to get the whole loop rolling — at your own pace.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss getting started checklist"
            className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition-colors"
            data-testid="getting-started-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Progress */}
        <div className="mt-4 flex items-center gap-3">
          <div className="h-1.5 flex-1 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
            <div
              className="h-full rounded-full bg-[hsl(var(--primary))] transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs tabular-nums text-muted-foreground shrink-0">
            {doneCount} of {total}
          </span>
        </div>

        {/* Steps */}
        <ul className="mt-4 space-y-2" data-testid="getting-started-steps">
          {steps.map((s) => {
            const isNext = nextStep && s.key === nextStep.key;
            return (
              <li
                key={s.key}
                className={cn(
                  // Wraps on phones: the button drops under the text instead
                  // of squeezing it to one word per line.
                  'flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 rounded-md border p-3 transition-colors',
                  s.done
                    ? 'border-border bg-[hsl(var(--muted))]/30'
                    : isNext
                    ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5'
                    : 'border-border'
                )}
                data-testid={`getting-started-step-${s.key}`}
                data-done={s.done ? 'true' : 'false'}
              >
                <span
                  className={cn(
                    'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                    s.done
                      ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                      : 'border-muted-foreground/40'
                  )}
                  aria-hidden
                >
                  {s.done ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                </span>
                <div className="min-w-0 flex-1 basis-[calc(100%-2rem)] sm:basis-auto">
                  <div
                    className={cn(
                      'text-sm font-medium leading-tight',
                      s.done ? 'text-muted-foreground line-through' : 'text-foreground'
                    )}
                  >
                    {s.label}
                  </div>
                  {!s.done ? (
                    <div className="text-xs text-muted-foreground mt-0.5">{s.desc}</div>
                  ) : null}
                </div>
                {!s.done ? (
                  <Button
                    asChild
                    size="sm"
                    variant={isNext ? 'default' : 'outline'}
                    className={cn(
                      'shrink-0 ml-8 sm:ml-0 uppercase tracking-[0.14em] text-[11px]',
                      isNext
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                        : 'border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10'
                    )}
                  >
                    <Link to={s.to} data-testid={`getting-started-cta-${s.key}`}>
                      {s.cta}
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Link>
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
